<script setup>
import {
  computed,
  ref,
  onBeforeMount,
  onMounted,
  onUnmounted,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { evaluateSLAStatus } from 'dashboard/helper/slaHelper';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import {
  snoozedReopenTimeToTimestamp,
  shortenSnoozeTime,
} from 'dashboard/helper/snoozeHelpers';

import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from 'next/polaris/ui/item';
import { Badge } from 'next/polaris/ui/badge';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import InboxContextMenu from 'dashboard/routes/dashboard/inbox/components/InboxContextMenu.vue';

const props = defineProps({
  inboxItem: { type: Object, default: () => ({}) },
  stateInbox: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'click',
  'contextMenuOpen',
  'contextMenuClose',
  'markNotificationAsRead',
  'markNotificationAsUnRead',
  'deleteNotification',
]);

const { t } = useI18n();

const isContextMenuOpen = ref(false);
const contextMenuPosition = ref({ x: null, y: null });

const primaryActor = computed(() => props.inboxItem?.primaryActor);
const meta = computed(() => primaryActor.value?.meta);
const sender = computed(() => meta.value?.sender || {});
const isUnread = computed(() => !props.inboxItem?.readAt);
const inbox = computed(() => props.stateInbox);

// SLA: the time left (or overdue) to answer, refreshed every minute while the row is on screen (as in the 3.1 row).
const SLA_REFRESH_INTERVAL = 60000;
const emptySla = { threshold: '', isSlaMissed: false, type: '' };
const slaStatus = ref(emptySla);
const slaTimer = ref(null);
const hasSlaPolicyId = computed(
  () => primaryActor.value?.appliedSla?.id && !sender.value?.blocked
);
const updateSlaStatus = () => {
  slaStatus.value = hasSlaPolicyId.value
    ? evaluateSLAStatus({
        appliedSla: primaryActor.value.appliedSla,
        chat: primaryActor.value,
        slaEvents: primaryActor.value.slaEvents || [],
      })
    : emptySla;
};
const scheduleSlaRefresh = () => {
  slaTimer.value = setTimeout(() => {
    updateSlaStatus();
    scheduleSlaRefresh();
  }, SLA_REFRESH_INTERVAL);
};
const showSla = computed(
  () => hasSlaPolicyId.value && !!slaStatus.value.threshold
);
const slaTitle = computed(() => {
  const type = slaStatus.value.type?.toUpperCase();
  if (!type) return '';
  return t(`CONVERSATION.HEADER.SLA_STATUS.${type}`, {
    status: t(
      `CONVERSATION.HEADER.SLA_STATUS.${slaStatus.value.isSlaMissed ? 'MISSED' : 'DUE'}`
    ),
  });
});
watch(primaryActor, updateSlaStatus);
onMounted(() => {
  updateSlaStatus();
  scheduleSlaRefresh();
});
onUnmounted(() => {
  if (slaTimer.value) clearTimeout(slaTimer.value);
});

const lastActivityAt = computed(() => {
  const timestamp = props.inboxItem?.lastActivityAt;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const menuItems = computed(() => [
  {
    key: isUnread.value ? 'mark_as_read' : 'mark_as_unread',
    icon: isUnread.value ? 'i-lucide-mail-open' : 'i-lucide-mail',
    label: t(`INBOX.MENU_ITEM.MARK_AS_${isUnread.value ? 'READ' : 'UNREAD'}`),
  },
  {
    key: 'delete',
    icon: 'i-lucide-trash-2',
    label: t('INBOX.MENU_ITEM.DELETE'),
  },
]);

// The push message reads "Sender: text" (or ": text" for a bare mention). The sender is kept, in medium weight.
const pushMessage = computed(() => {
  const body = props.inboxItem?.pushMessageBody || '';
  if (body.startsWith(': ')) return { name: '', text: body.slice(2) };
  const match = body.match(/^([^:]+):\s?(.*)$/s);
  return match
    ? { name: `${match[1]}:`, text: match[2] }
    : { name: '', text: body };
});

const notificationDetails = computed(() => {
  const type = props.inboxItem?.notificationType?.toUpperCase() || '';
  return {
    text: type ? t(`INBOX.TYPES_NEXT.${type}`) : '',
    isSla: type.startsWith('SLA_'),
  };
});

const snoozedUntilTime = computed(() => {
  const { snoozedUntil } = props.inboxItem;
  if (!snoozedUntil) return null;
  return shortenSnoozeTime(
    dynamicTime(snoozedReopenTimeToTimestamp(snoozedUntil))
  );
});

const hasLastSnoozed = computed(() => props.inboxItem?.meta?.lastSnoozedAt);

const snoozedText = computed(() => {
  return !hasLastSnoozed.value
    ? t('INBOX.TYPES_NEXT.SNOOZED_UNTIL', {
        time: shortTimestamp(snoozedUntilTime.value),
      })
    : t('INBOX.TYPES_NEXT.SNOOZED_ENDS');
});

const contextMenuActions = {
  close: () => {
    isContextMenuOpen.value = false;
    contextMenuPosition.value = { x: null, y: null };
    emit('contextMenuClose');
  },
  open: e => {
    e.preventDefault();
    contextMenuPosition.value = {
      x: e.pageX || e.clientX,
      y: e.pageY || e.clientY,
    };
    isContextMenuOpen.value = true;
    emit('contextMenuOpen');
  },
  handle: key => {
    const actions = {
      mark_as_read: () => emit('markNotificationAsRead', props.inboxItem),
      mark_as_unread: () => emit('markNotificationAsUnRead', props.inboxItem),
      delete: () => emit('deleteNotification', props.inboxItem),
    };
    actions[key]?.();
  },
};

onBeforeMount(contextMenuActions.close);
</script>

<template>
  <Item
    size="sm"
    role="button"
    class="w-full cursor-pointer flex-nowrap items-start px-3 text-start text-polaris-foreground hover:bg-polaris-accent aria-[current=true]:bg-polaris-selected aria-[current=true]:text-polaris-selected-foreground"
    @contextmenu="contextMenuActions.open($event)"
    @click="emit('click')"
  >
    <!-- One notification: the 3.1 row's shape (Item) with the notification's data. The root keeps `role="button"`; the
      list adds `.inbox-card` and `.active` (scrollActiveIntoView finds the open one) and `aria-current` (the selected look). -->
    <ItemMedia>
      <Avatar
        :name="sender.name"
        :src="sender.thumbnail"
        :size="32"
        rounded-full
      />
    </ItemMedia>
    <ItemContent class="min-w-0 gap-0.5">
      <ItemTitle
        class="w-full justify-between"
        :class="isUnread ? 'font-medium' : 'font-normal'"
      >
        <span class="flex min-w-0 items-center gap-1.5">
          <span
            v-if="isUnread"
            class="size-2 shrink-0 rounded-full bg-polaris-primary"
            aria-hidden="true"
          />
          <span v-if="isUnread" class="sr-only">
            {{ $t('INBOX.LIST.UNREAD') }}
          </span>
          <span
            class="truncate"
            :class="{ 'text-polaris-destructive': notificationDetails.isSla }"
          >
            {{ notificationDetails.text }}
          </span>
        </span>
        <span
          class="flex shrink-0 items-center gap-1.5 text-xs font-normal text-polaris-muted-foreground group-aria-[current=true]/item:text-polaris-selected-foreground"
        >
          <CardPriorityIcon
            v-if="primaryActor?.priority"
            :priority="primaryActor.priority"
            class="!size-3.5 shrink-0"
          />
          {{ lastActivityAt }}
        </span>
      </ItemTitle>
      <p
        class="mb-0 flex min-w-0 items-center gap-1.5 type-polaris-small-label text-polaris-muted-foreground group-aria-[current=true]/item:text-polaris-selected-foreground"
      >
        <span class="shrink-0 font-medium">{{ `#${primaryActor?.id}` }}</span>
        <template v-if="sender.name">
          <span
            class="size-0.5 shrink-0 rounded-full bg-current"
            aria-hidden="true"
          />
          <span class="truncate">{{ sender.name }}</span>
        </template>
      </p>
      <p
        v-if="inbox?.name"
        class="mb-0 flex min-w-0 items-center gap-1.5 text-xs text-polaris-muted-foreground group-aria-[current=true]/item:text-polaris-selected-foreground"
      >
        <ChannelIcon v-if="inbox.id" :inbox="inbox" class="size-3.5 shrink-0" />
        <span class="truncate" :title="inbox.name">{{ inbox.name }}</span>
      </p>
      <!-- One line, cut with an ellipsis where the row ends. -->
      <ItemDescription
        class="mb-0 line-clamp-none text-xs"
        :class="
          isUnread
            ? 'text-polaris-foreground group-aria-[current=true]/item:text-polaris-selected-foreground'
            : 'group-aria-[current=true]/item:text-polaris-selected-foreground'
        "
      >
        <span class="block truncate">
          <span v-if="pushMessage.name" class="font-medium">
            {{ pushMessage.name }}
          </span>
          {{ pushMessage.text }}
        </span>
      </ItemDescription>
      <div
        v-if="showSla || snoozedUntilTime || hasLastSnoozed"
        class="flex flex-wrap items-center gap-1 pt-1"
      >
        <Badge
          v-if="showSla"
          :variant="slaStatus.isSlaMissed ? 'destructive' : 'outline'"
          :title="slaTitle"
        >
          <Icon icon="i-lucide-clock" />
          {{ slaStatus.threshold }}
        </Badge>
        <Badge
          v-if="snoozedUntilTime || hasLastSnoozed"
          variant="outline"
          class="max-w-full"
        >
          <Icon
            :icon="
              !hasLastSnoozed
                ? 'i-lucide-alarm-clock-plus'
                : 'i-lucide-alarm-clock-off'
            "
          />
          <span class="truncate">{{ snoozedText }}</span>
        </Badge>
      </div>
    </ItemContent>
    <InboxContextMenu
      v-if="isContextMenuOpen"
      :context-menu-position="contextMenuPosition"
      :menu-items="menuItems"
      @close="contextMenuActions.close"
      @select-action="contextMenuActions.handle"
    />
  </Item>
</template>
